import { useMemo } from "react";
import type { FormAnchor } from "./use-week-calendar-state";

export interface SmartPanelPosition {
  left: number;
  top: number;
  isMobile: boolean;
  isTablet: boolean;
}

export interface SmartPanelOptions {
  readonly formAnchor?: FormAnchor | null;
}

const PANEL_WIDTH = 448; // max-w-md = 28rem = 448px
const VIEWPORT_MARGIN = 16;

/**
 * Calcule la position initiale du panneau en fonction de l'ancre.
 * - Mobile (<768px) : full-screen, pas de positionnement
 * - Tablet (768-1024px) : centré
 * - Desktop (>1024px) : ancré à droite ou gauche de la colonne du créneau
 */
export function useSmartPanelPosition(options: SmartPanelOptions = {}): SmartPanelPosition {
  const { formAnchor } = options;

  // Tablet gets the same full-screen treatment as mobile (no draggable anchored panel)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const isTablet = typeof window !== "undefined" && window.innerWidth >= 768 && window.innerWidth < 1024;

  return useMemo(() => {
    if (isMobile) {
      return { left: 0, top: 0, isMobile: true, isTablet: false };
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!formAnchor || isTablet) {
      // Centré
      return {
        left: Math.max(VIEWPORT_MARGIN, (vw - PANEL_WIDTH) / 2),
        top: Math.max(VIEWPORT_MARGIN, vh * 0.08),
        isMobile: false,
        isTablet,
      };
    }

    // Desktop avec ancre : essayer à droite de la colonne, sinon à gauche
    const gap = 16;
    let left: number;
    const spaceRight = vw - formAnchor.colRight - gap;
    const spaceLeft = formAnchor.colLeft - gap;

    if (spaceRight >= PANEL_WIDTH + VIEWPORT_MARGIN) {
      // Assez de place à droite
      left = formAnchor.colRight + gap;
    } else if (spaceLeft >= PANEL_WIDTH + VIEWPORT_MARGIN) {
      // Assez de place à gauche
      left = formAnchor.colLeft - PANEL_WIDTH - gap;
    } else {
      // Pas assez de place ni à droite ni à gauche — centrer
      left = Math.max(VIEWPORT_MARGIN, (vw - PANEL_WIDTH) / 2);
    }

    // Position idéale = hauteur du slot cliqué (le clampage réel se fait dans DraggableEventPanel)
    const top = formAnchor.slotTop;

    return { left, top, isMobile: false, isTablet: false };
  }, [formAnchor, isMobile, isTablet]);
}
